import { Card } from "@/shared/components/ui/card"
import type { StatisticsNight } from "./api/statistics.queries"
import {
  clockMinutesToY,
  clockToMinutes,
  DRIFT,
  driftSvgHeight,
  formatDayLabel,
  labelStride,
  PLOT,
  pointX,
} from "./statistics.config"

interface DriftChartProps {
  nights: StatisticsNight[]
}

const HOUR_TICKS = [0, 6, 12, 18, 24]

export function DriftChart({ nights }: DriftChartProps) {
  const hasData = nights.some((n) => n.bedtime !== null || n.wakeTime !== null)
  if (!hasData) {
    return (
      <Card className="items-center py-8 text-sm text-muted-foreground">
        Inga nätter registrerade ännu
      </Card>
    )
  }

  const svgHeight = driftSvgHeight()
  const stride = labelStride(nights.length)

  return (
    <Card className="gap-2 py-4">
      <div className="flex items-center justify-between px-4">
        <h2 className="text-sm font-semibold">Läggning & uppvaknande</h2>
        <div className="flex gap-3 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="size-2 rounded-full bg-accent" />
            Läggning
          </span>
          <span className="flex items-center gap-1">
            <span className="size-2 rounded-full bg-score-negative" />
            Uppvaknande
          </span>
        </div>
      </div>
      <div className="flex px-2">
        <svg
          width={DRIFT.leftAxis}
          height={svgHeight}
          className="shrink-0 text-muted-foreground"
          role="presentation"
        >
          {HOUR_TICKS.map((h) => (
            <text
              key={h}
              x={DRIFT.leftAxis - 6}
              y={clockMinutesToY(h * 60) + 4}
              textAnchor="end"
              className="fill-current text-xs"
            >
              {String(h).padStart(2, "0")}
            </text>
          ))}
        </svg>

        <svg
          viewBox={`0 0 ${PLOT.width} ${svgHeight}`}
          className="h-auto w-full"
          role="img"
          aria-label="Läggning och uppvaknande"
        >
          {HOUR_TICKS.map((h) => (
            <line
              key={h}
              x1={0}
              x2={PLOT.width}
              y1={clockMinutesToY(h * 60)}
              y2={clockMinutesToY(h * 60)}
              className="stroke-border"
              strokeWidth={1}
            />
          ))}
          {nights.map((night, i) => {
            const bedtime = clockToMinutes(night.bedtime)
            const wake = clockToMinutes(night.wakeTime)
            return (
              <g key={night.date}>
                {bedtime !== null && (
                  <circle
                    cx={pointX(i, nights.length)}
                    cy={clockMinutesToY(bedtime)}
                    r={DRIFT.dotRadius}
                    className="fill-accent"
                  />
                )}
                {wake !== null && (
                  <circle
                    cx={pointX(i, nights.length)}
                    cy={clockMinutesToY(wake)}
                    r={DRIFT.dotRadius}
                    className="fill-score-negative"
                  />
                )}
              </g>
            )
          })}
          {nights.map((night, i) =>
            i % stride === 0 ? (
              <text
                key={night.date}
                x={pointX(i, nights.length)}
                y={svgHeight - 8}
                textAnchor="middle"
                className="fill-muted-foreground text-[9px]"
              >
                {formatDayLabel(night.date)}
              </text>
            ) : null,
          )}
        </svg>
      </div>
    </Card>
  )
}
